import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import LoadingCard from "components/LoadingCard/LoadingCard";
import ListSearch from "components/ListSearch/ListSearch";
import { useTextFilter } from "helpers/textFilter";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import { uploadImage } from "pages/MarketingPages/pagesApi";
import { swapPatches } from "./promotionOrder";
import { isValidLink } from "./promotionValidation";
import {
  createPromotion,
  deletePromotion,
  errorMessage,
  fetchPromotions,
  setPromotionStatus,
  updatePromotion,
} from "./promotionsApi";

/** Same 44px minimum as CONTROL, applied to the switch + label pair. */
const CURRENT_ROW = { ...CONTROL, display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" };

const { Title, Text } = Typography;

const promotionSearchFields = (promotion) => [promotion.title, promotion.titleEn, promotion.link];

/**
 * "Актуално за месеца" — the promotions strip on the landing site.
 *
 * Publishing and the "Актуална" (current) flag are deliberately separate
 * controls, the same split Testimonials and PromoBanners use: a save can
 * never publish by accident, and marking something "current" is its own
 * decision that doesn't require re-opening the editor.
 */
export default function Promotions() {
  const [promotions, setPromotions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Id of the promotion whose "current" switch is mid-flight, so a second
  // tap cannot fire a competing PATCH that resolves out of order.
  const [togglingId, setTogglingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      // Already in site order — the API sorts by `order`, then newest.
      setPromotions((await fetchPromotions()) || []);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isNew = editing != null && !editing._id;

  const openEditor = (promotion) => {
    setEditing(promotion ?? {});
    form.setFieldsValue({
      title: promotion?.title ?? "",
      titleEn: promotion?.titleEn ?? "",
      image: promotion?.image ?? "",
      link: promotion?.link ?? "",
      current: promotion?.current ?? false,
      endsAt: promotion?.endsAt ? dayjs(promotion.endsAt) : null,
    });
  };

  const closeEditor = () => {
    setEditing(null);
    form.resetFields();
  };

  const submit = async (values) => {
    setIsSaving(true);
    try {
      const payload = {
        title: values.title,
        titleEn: values.titleEn || "",
        image: values.image,
        link: values.link,
        current: Boolean(values.current),
        // null rather than undefined: clearing the date has to reach the API
        // as a value, or a promotion can never lose its end date again.
        // End of the chosen day, not its midnight: the picker returns local
        // 00:00, which as UTC is the evening before, and the site would drop
        // the promotion a whole day early.
        endsAt: values.endsAt ? values.endsAt.endOf("day").toISOString() : null,
      };

      if (isNew) {
        // No `order` in the payload: the API appends new promotions to the
        // end of the row (max+1), so every writer gets the same behaviour.
        await createPromotion(payload);
      } else {
        await updatePromotion(editing._id, payload);
      }

      message.success("Запазено");
      closeEditor();
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const changeStatus = async (promotion, status) => {
    try {
      await setPromotionStatus(promotion._id, status);
      message.success(status === "published" ? "Публикувано на сайта" : "Свалено от сайта");
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    }
  };

  const toggleCurrent = async (promotion, current) => {
    setTogglingId(promotion._id);
    try {
      await updatePromotion(promotion._id, { current });
      message.success(current ? "Отбелязано като актуално" : "Вече не е актуално");
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setTogglingId(null);
      await load();
    }
  };

  const remove = async (promotion) => {
    try {
      await deletePromotion(promotion._id);
      message.success("Изтрито");
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    }
  };

  const visiblePromotions = useTextFilter(promotions, promotionSearchFields, searchQuery);
  // Moving a row while the list is filtered would swap it with a neighbour the
  // admin can't see, so reordering waits until the search is cleared (as FAQ).
  const isSearchActive = Boolean(searchQuery);

  const move = async (promotion, direction) => {
    const patches = swapPatches(promotions, promotion._id, direction);
    if (!patches.length) return;
    try {
      // The two patches target distinct rows, so they can run together.
      await Promise.all(patches.map((patch) => updatePromotion(patch.id, { order: patch.order })));
      await load();
    } catch (error) {
      message.error(errorMessage(error));
      await load();
    }
  };

  const uploadPromoImage = async (file) => {
    setIsUploading(true);
    try {
      const url = await uploadImage(file);
      if (!url) throw new Error("Качването не върна URL адрес");
      form.setFieldsValue({ image: url });
    } catch (error) {
      message.error(`Неуспешно качване: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 720, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Промоции
        </Title>
        <Text type="secondary">
          &quot;Актуално за месеца&quot; на сайта, показани в този ред. Отбележи една като
          &quot;Актуална&quot;, за да я откроиш; публикувай, за да е видима изобщо.
        </Text>
      </Space>

      <Button
        type="primary"
        block
        style={{ ...CONTROL, marginBottom: 16 }}
        onClick={() => openEditor(null)}
      >
        Нова промоция
      </Button>

      {isLoading && <LoadingCard />}

      {!isLoading && !loadError && (
        <ListSearch
          value={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Търсене по заглавие"
          className="mb-3"
        />
      )}

      {!isLoading && !loadError && isSearchActive && (
        <Text type="secondary" className="d-block mb-3">
          Изчистете търсенето, за да пренареждате.
        </Text>
      )}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на промоциите"
          description={loadError}
          action={<Button onClick={load}>Опитай отново</Button>}
        />
      )}

      {!isLoading && !loadError && (
        <List
          bordered
          dataSource={visiblePromotions}
          locale={{
            emptyText: searchQuery ? "Няма промоции, отговарящи на търсенето." : "Все още няма промоции.",
          }}
          renderItem={(promotion, index) => (
            <List.Item style={{ display: "block", padding: 16 }}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Space wrap size={8}>
                  {promotion.status === "published" ? (
                    <Tag color="green">Публикувано</Tag>
                  ) : (
                    <Tag>Чернова</Tag>
                  )}
                  {promotion.endsAt && (
                    <Text type="secondary">
                      До {dayjs(promotion.endsAt).format("D MMM YYYY")}
                    </Text>
                  )}
                </Space>

                <Space size={12} align="start" style={{ width: "100%" }}>
                  {promotion.image && (
                    <img
                      src={promotion.image}
                      alt=""
                      style={{
                        width: 56,
                        height: 56,
                        objectFit: "cover",
                        borderRadius: 4,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Space direction="vertical" size={0}>
                    <Text strong>{promotion.title}</Text>
                    {promotion.titleEn && <Text type="secondary">{promotion.titleEn}</Text>}
                    <Text type="secondary">{promotion.link}</Text>
                  </Space>
                </Space>

                {/* A label element, not a Text beside the switch: the tap
                    target becomes the whole 44px row and the switch is
                    announced by name. */}
                <label style={CURRENT_ROW}>
                  <Switch
                    checked={Boolean(promotion.current)}
                    loading={togglingId === promotion._id}
                    disabled={togglingId !== null && togglingId !== promotion._id}
                    aria-label="Актуална"
                    onChange={(checked) => toggleCurrent(promotion, checked)}
                  />
                  <Text>Актуална</Text>
                </label>

                <Space wrap size={8}>
                  <Button
                    style={CONTROL}
                    icon={<ArrowUpOutlined />}
                    aria-label="Премести нагоре"
                    disabled={isSearchActive || index === 0}
                    onClick={() => move(promotion, "up")}
                  />
                  <Button
                    style={CONTROL}
                    icon={<ArrowDownOutlined />}
                    aria-label="Премести надолу"
                    disabled={isSearchActive || index === promotions.length - 1}
                    onClick={() => move(promotion, "down")}
                  />
                  <Button style={CONTROL} onClick={() => openEditor(promotion)}>
                    Редактирай
                  </Button>
                  {promotion.status === "published" ? (
                    <Button style={CONTROL} onClick={() => changeStatus(promotion, "draft")}>
                      Свали от сайта
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      style={CONTROL}
                      onClick={() => changeStatus(promotion, "published")}
                    >
                      Публикувай
                    </Button>
                  )}
                  <Popconfirm
                    title="Сигурен ли си, че искаш да изтриеш тази промоция?"
                    onConfirm={() => remove(promotion)}
                    okText="Да"
                    cancelText="Отказ"
                  >
                    <Button danger style={CONTROL}>
                      Изтрий
                    </Button>
                  </Popconfirm>
                </Space>
              </Space>
            </List.Item>
          )}
        />
      )}

      <Modal
        open={editing != null}
        title={isNew ? "Нова промоция" : "Редактиране на промоция"}
        onCancel={closeEditor}
        onOk={form.submit}
        confirmLoading={isSaving}
        // "Запази", never "Публикувай" — the list's Publish button is the only way live.
        okText="Запази"
        cancelText="Отказ"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
          <Form.Item
            name="title"
            label="Заглавие"
            rules={[
              { required: true, whitespace: true, message: "Заглавието е задължително" },
              { max: 200, message: "Заглавието трябва да е до 200 символа" },
            ]}
          >
            <Input style={CONTROL} maxLength={200} />
          </Form.Item>

          <Form.Item
            name="titleEn"
            label="Заглавие (на английски, незадължително)"
            rules={[{ max: 200, message: "Заглавието трябва да е до 200 символа" }]}
          >
            <Input style={CONTROL} maxLength={200} />
          </Form.Item>

          <Form.Item
            name="image"
            label="Снимка"
            rules={[{ required: true, whitespace: true, message: "Снимката е задължителна" }]}
          >
            <Input
              style={CONTROL}
              placeholder="/media/…"
              addonAfter={
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    uploadPromoImage(file);
                    return false;
                  }}
                >
                  <Button size="small" type="text" icon={<UploadOutlined />} loading={isUploading}>
                    Качи
                  </Button>
                </Upload>
              }
            />
          </Form.Item>

          <Form.Item
            name="link"
            label="Връзка"
            extra="Адрес в сайта, напр. /koledna-igra-s-nagradi/, или пълен https:// адрес."
            rules={[
              { required: true, whitespace: true, message: "Връзката е задължителна" },
              {
                validator: (_, value) =>
                  !value || isValidLink(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Трябва да започва с / или https://")),
              },
            ]}
          >
            <Input style={CONTROL} placeholder="/koledna-igra-s-nagradi/" />
          </Form.Item>

          <Form.Item
            name="endsAt"
            label="Крайна дата (незадължително)"
            extra="Остави празно за промоция без крайна дата."
          >
            <DatePicker style={{ ...CONTROL, width: "100%" }} allowClear />
          </Form.Item>

          <Form.Item name="current" label="Актуална" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
